import React, { useState, useEffect } from "react";
import { Edit2, Check, X } from "lucide-react";
import IconButton from "./Button/IconButton.jsx";
import { Button } from "./Button/Button.jsx";
import Textarea from "./Textarea.jsx";

/**
 * EditableTextarea - Reusable inline editable textarea component
 * 
 * Provides inline editing functionality for multi-line text with consistent
 * styling matching EditableField. Uses lucide-react icons.
 * 
 * @param {Object} props
 * @param {string} props.initialValue - Initial value to display
 * @param {Function} props.onSave - Callback when value is saved (receives new value)
 * @param {string} props.className - Additional CSS classes for the display value
 * @param {number} props.rows - Number of rows for textarea (default: 4)
 * @param {string} props.placeholder - Placeholder text (optional)
 */
const EditableTextarea = ({
    initialValue,
    onSave,
    className = "text-muted-foreground",
    rows = 4,
    placeholder = "",
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState(initialValue);

    // Sync internal state with prop changes
    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    const handleSave = () => {
        onSave(value);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setValue(initialValue);
        setIsEditing(false);
    };

    return isEditing ? (
        <div className="flex flex-col gap-2">
            <Textarea
                value={value}
                onChange={(e) => setValue(e.target.value)}
                rows={rows}
                placeholder={placeholder}
                className={`resize-none ${className}`}
                autoFocus
            />
            <div className="flex justify-end gap-2">
                <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleCancel}
                    className="text-gray-500 hover:text-gray-700"
                >
                    Cancel
                </Button>
                <Button
                    size="sm"
                    onClick={handleSave}
                    className="bg-red-400 hover:bg-red-500 text-white"
                >
                    Save
                </Button>
            </div>
        </div>
    ) : (
        <div className="flex items-start gap-2 group">
            <p className={`whitespace-pre-wrap ${className}`}>{value || placeholder}</p>
            <IconButton
                icon={<Edit2 className="h-4 w-4" />}
                size="sm"
                variant="ghost"
                onClick={() => setIsEditing(true)}
                className="opacity-5 group-hover:opacity-100 transition-opacity duration-200 mt-1"
            />
        </div>
    );
};

export default EditableTextarea;
